import { HttpErrorResponse } from "@angular/common/http";

import { User } from "@core/models/user.model";
import { createReducer, on } from "@ngrx/store";

import * as AuthActions from "./auth.actions";

export const AUTH_FEATURE_KEY = "auth";

/**
 * Which two-factor screen the login view should show while an MFA-pending token is held:
 * - "loading": looking up whether the user is enrolled;
 * - "challenge": enrolled — ask for the authenticator code;
 * - "enroll": not enrolled — show the QR/secret to scan, then confirm with a code;
 * - "recovery-display": enrollment just succeeded — show the one-time recovery codes.
 */
export type MfaStage =
  | "loading"
  | "challenge"
  | "enroll"
  | "recovery-display"
  | null;

export interface AuthState {
  user: User | undefined;
  loading: boolean;
  loginError: HttpErrorResponse | null;
  // Two-factor challenge state. mfaToken is the short-lived, in-memory pending-login token; while it
  // is set the login view shows a two-factor screen (which one is given by mfaStage) instead of the
  // password form. mfaTarget is the route to navigate to once the flow completes. mfaError flags a
  // failed setup/verify/enable so the active screen can surface it. The enroll fields carry the
  // authenticator secret/QR; mfaRecoveryCodes holds the one-time codes shown right after enrollment.
  mfaToken: string | null;
  mfaTarget: string | null;
  mfaStage: MfaStage;
  mfaSharedKey: string | null;
  mfaAuthenticatorUri: string | null;
  mfaRecoveryCodes: string[];
  mfaError: HttpErrorResponse | null;
}

const initialState: AuthState = {
  user: undefined,
  loading: false,
  loginError: null,
  mfaToken: null,
  mfaTarget: null,
  mfaStage: null,
  mfaSharedKey: null,
  mfaAuthenticatorUri: null,
  mfaRecoveryCodes: [],
  mfaError: null,
};

/** Clears every trace of an in-flight two-factor flow. */
const clearMfa = (state: AuthState): AuthState => {
  return {
    ...state,
    mfaToken: null,
    mfaTarget: null,
    mfaStage: null,
    mfaSharedKey: null,
    mfaAuthenticatorUri: null,
    mfaRecoveryCodes: [],
    mfaError: null,
  };
};

export const AUTH_REDUCER = createReducer(
  initialState,

  on(AuthActions.DO_LOGIN, (state: AuthState): AuthState => {
    // A fresh password attempt supersedes any pending two-factor flow.
    return clearMfa({
      ...state,
      loading: true,
      loginError: null,
    });
  }),

  on(
    AuthActions.LOGIN_SUCCESS,
    (state: AuthState, action: { user: User }): AuthState => {
      return clearMfa({
        ...state,
        loading: false,
        loginError: null,
        user: action.user,
      });
    }
  ),

  on(
    AuthActions.LOGIN_FAILED,
    (state: AuthState, action: { error: HttpErrorResponse }): AuthState => {
      return {
        ...state,
        loading: false,
        loginError: action.error,
      };
    }
  ),

  on(
    AuthActions.SET_LOGIN_LOADING,
    (state: AuthState, action: { loading: boolean }): AuthState => {
      return {
        ...state,
        loading: action.loading,
      };
    }
  ),

  on(
    AuthActions.SET_USER_DATA,
    (state: AuthState, action: { user: User }): AuthState => {
      return {
        ...state,
        user: action.user,
      };
    }
  ),

  on(
    AuthActions.LOGIN_MFA_REQUIRED,
    (
      state: AuthState,
      action: { mfaToken: string; target: string }
    ): AuthState => {
      return {
        ...clearMfa(state),
        loading: true,
        loginError: null,
        mfaToken: action.mfaToken,
        mfaTarget: action.target,
        mfaStage: "loading",
      };
    }
  ),

  on(
    AuthActions.MFA_SETUP_LOADED,
    (
      state: AuthState,
      action: {
        enrolled: boolean;
        sharedKey?: string;
        authenticatorUri?: string;
      }
    ): AuthState => {
      return {
        ...state,
        loading: false,
        mfaError: null,
        mfaStage: action.enrolled ? "challenge" : "enroll",
        mfaSharedKey: action.sharedKey ?? null,
        mfaAuthenticatorUri: action.authenticatorUri ?? null,
      };
    }
  ),

  on(
    AuthActions.VERIFY_MFA,
    AuthActions.ENABLE_MFA,
    AuthActions.USE_RECOVERY_CODE,
    AuthActions.MFA_RECOVERY_ACKNOWLEDGED,
    (state: AuthState): AuthState => {
      return {
        ...state,
        loading: true,
        mfaError: null,
      };
    }
  ),

  on(
    AuthActions.MFA_ENABLED,
    (state: AuthState, action: { recoveryCodes: string[] }): AuthState => {
      return {
        ...state,
        loading: false,
        mfaError: null,
        mfaStage: "recovery-display",
        mfaRecoveryCodes: action.recoveryCodes,
      };
    }
  ),

  on(
    AuthActions.MFA_FAILED,
    (state: AuthState, action: { error: HttpErrorResponse }): AuthState => {
      return {
        ...state,
        loading: false,
        mfaError: action.error,
      };
    }
  ),

  on(AuthActions.CANCEL_MFA, (state: AuthState): AuthState => {
    return clearMfa({
      ...state,
      loading: false,
    });
  }),

  on(AuthActions.LOGOUT_SUCCESS, (): AuthState => initialState)
);
